<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { onMounted, computed, ref, onUnmounted, watch } from 'vue';
import Point from './DraftImage/Point.vue';
import DrawingArea from './DraftImage/DrawingArea.vue';
import Messenger from './DraftImage/Messenger.vue';
import DraftHelp from './_components/DraftHelp.vue';
import LastComment from './DraftImage/LastComment.vue';
import RightMouseMenu from './DraftImage/RightMouseMenu.vue';
import { onClickOutside, useMediaQuery } from '@vueuse/core';

import { showNotification } from '../../notifications/notification_actions';
import DraftZoom from './DraftImage/DraftZoom.vue';
import DraftHeader from '@/dashboard/vue/components/layout/DraftHeader/DraftHeader.vue';
import { useDraftStore } from '@/dashboard/store/draftStore';
import { useDraftImageStore } from '@/dashboard/store/draftImageStore';
import IconButton from '@/dashboard/vue/components/common/IconButton.vue';
import { DraftType } from '@/dashboard/types/draft';

const isSmartphone = useMediaQuery('(max-width: 1093px)');

const imageRef = ref<HTMLImageElement | null>(null);
const imagePresenterRef = ref<HTMLElement | null>(null);
const showBottomHelpBtn = ref<HTMLElement | null>(null);

const draftStore = useDraftStore();
const draftImageStore = useDraftImageStore();

const { draft, downloadUrl, imageUrl, imageScaleDownMode, imageAutoscaling, allowCommenting } = storeToRefs(draftStore);
const {
  points,
  drawingPoint,
  activePoint,
  hoverPoint,
  messengerLoading,
  originalImageSize,
  imageSize,
  defaultColor,
  showNewDiscussionMessage,
  imageZoom,
  blurImageUrl,
  imageScaledSize,
  blurPercent,
  showBottomHelp,
  hideAll,
  deletedPoints,
  rightMousePoint,
} = storeToRefs(draftImageStore);

const loading = ref(true);

const coloredPoints = computed(() => {
  return points.value.filter((item) => {
    return item.color !== 10 && !isDeletedPoints(item.id);
  });
});

const invertedPoints = computed(() => {
  return points.value.filter((item) => {
    return item.color === 10 && !isDeletedPoints(item.id);
  });
});

function isDeletedPoints(id: any) {
  return [...deletedPoints.value].indexOf(id) !== -1;
}

function openPoint(point: any) {
  if (messengerLoading.value) {
    return;
  }
  const currentPoint = activePoint.value;

  if (!!currentPoint && currentPoint.id === point.id) {
    draftImageStore.closeTempPoint();
    return;
  }

  draftImageStore.setActivePoint(point);
  draftImageStore.setDrawingPoint(null);

  if (!point.read) {
    draftImageStore.readPointComments();
  }
}

async function loadDraft() {
  const shortLinkInput = document.querySelector("input[name='short_link']") as HTMLInputElement | null;
  const shortLink = shortLinkInput?.value || '';

  if (shortLink) {
    const response = await draftStore.loadDraftByShortLink(shortLink);
    if (response.success && response.points) {
      draftImageStore.setPoints(response.points);

      draftImageStore.setDeletedPoints([]);
      draftImageStore.setDeletedComments([]);
    }
  }
}

function showBottomHelpClick() {
  draftImageStore.toggleBottomHelp();
}

function documentKeydown(event: KeyboardEvent) {
  if (event.key === 'Tab') {
    event.preventDefault();
    draftImageStore.setHideAll(!hideAll.value);
    return;
  }

  // Alt + C (Windows) or Option + C (Mac)
  if (event.altKey && event.code === 'KeyC') {
    event.preventDefault();
    draftImageStore.openCommentHotkey();
    return;
  }

  if (event.key === 'Escape') {
    event.preventDefault();
    draftImageStore.closeTempPoint();
  }
}

function clickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (target && target.classList.contains('draw-image')) {
    draftImageStore.closeTempPoint();
    draftImageStore.setRightMousePoint(null);
  }
}

function pointMouseOver(point: any) {
  draftImageStore.setHoverPoint(point);
}

function pointMouseOut() {
  draftImageStore.setHoverPoint(null);
}

function calculateSize() {
  draftImageStore.calculateSize(imageScaleDownMode.value);
}

onClickOutside(showBottomHelpBtn, (event) => draftImageStore.toggleBottomHelp(false));

watch([imageScaleDownMode], calculateSize);

onMounted(async () => {
  calculateSize();

  window.addEventListener('resize', calculateSize);
  document.addEventListener('keydown', documentKeydown);

  if (imageAutoscaling.value) {
    draftStore.setScaleDownMode(imageZoom.value > 1, DraftType.image);
  }

  await loadDraft();
  loading.value = false;

  if (imagePresenterRef.value) {
    imagePresenterRef.value.addEventListener('mousedown', clickOutside);
  }

  const hash = window.location.hash;
  if (hash) {
    const id = +hash.substring(1);
    const point = points.value.find((p: any) => {
      return p.comments.map((c: any) => c.comment.id).indexOf(id) > -1;
    });

    if (point) {
      openPoint(point);

      setTimeout(() => {
        const evt = new CustomEvent('scrollToComment', { detail: id });
        window.dispatchEvent(evt);
      }, 10);
    }
  }

  if (!allowCommenting.value) {
    showNotification('draft_comment_disabled', {
      emoji: '🔒',
      title: isSmartphone.value ? 'Commenting disabled' : 'The author has disabled commenting and creating new comments',
      ok_action: () => {},
    });
  } else if (draft.value?.owner_id !== (window as any).g_opt.user.id) {
    if (showNewDiscussionMessage.value && !isSmartphone.value) {
      showNotification('draft_comment_disabled', {
        emoji: '❕',
        title: 'Clicking on the screenshot will initiate a new discussion point if the author allows it',
        ok_action: () => {
          draftStore.changeShowDrawingMessage(false);
        },
      });
    }
  }
});

onUnmounted(() => {
  if (imagePresenterRef.value) {
    imagePresenterRef.value.removeEventListener('mousedown', clickOutside);
  }
  document.removeEventListener('keydown', documentKeydown);
  window.removeEventListener('resize', calculateSize);
});
</script>

<template>
  <div class="h-[var(--app-height)] overflow-hidden">
    <div class="draft-view bg-gray-100 dark:bg-gray-975">
      <div v-if="loading" class="draft-view__loader">
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none" class="rotate">
          <path
            class="stroke-black dark:stroke-gray-500"
            d="M20.5832 11C20.5832 16.2927 16.2926 20.5834 10.9998 20.5834C5.70711 20.5834 1.4165 16.2927 1.4165 11C1.4165 5.70729 5.70711 1.41669 10.9998 1.41669"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <DraftHeader v-if="!hideAll" :draft="draft" :disabled="!draft" :show-menu-button="true" />

      <div ref="imagePresenterRef" class="draft-view__image-presenter max-tablet:px-s">
        <div class="draw-image" :class="{ show: !loading }">
          <div v-if="draft" class="draw-image__content shadow-s">
            <DraftZoom
              :width="imageScaledSize.width"
              :height="imageScaledSize.height"
              :original-width="imageScaleDownMode ? imageSize.width : originalImageSize.width"
              :original-height="imageScaleDownMode ? imageSize.height : originalImageSize.height"
            >
              <template #default="slotProps">
                <img ref="imageRef" :src="imageUrl || undefined" alt="" class="h-full w-full" />

                <div v-if="!hideAll" class="draw-image__content__draw">
                  <div :class="{ 'mix-blend-difference': defaultColor === 10 }">
                    <DrawingArea :zoom="slotProps.zoom"></DrawingArea>
                  </div>

                  <div class="mix-blend-difference">
                    <Point
                      v-for="point in invertedPoints"
                      :key="point"
                      :point="point"
                      @click="openPoint(point)"
                      @mouseover="pointMouseOver(point)"
                      @mouseout="pointMouseOut"
                    />
                  </div>

                  <Point
                    v-for="point in coloredPoints"
                    :key="point"
                    :point="point"
                    @click="openPoint(point)"
                    @mouseover="pointMouseOver(point)"
                    @mouseout="pointMouseOut"
                  />

                  <div v-if="drawingPoint" :class="{ 'mix-blend-difference': drawingPoint.color === 10 }">
                    <Point :point="drawingPoint" />
                  </div>

                  <RightMouseMenu
                    v-if="rightMousePoint && downloadUrl"
                    :draft="draft"
                    :point="rightMousePoint"
                    :zoom="slotProps.zoom"
                    :download-url="downloadUrl"
                  />
                </div>

                <Messenger v-if="activePoint && !hideAll" :point="activePoint" :loading="messengerLoading" :zoom="slotProps.zoom" />

                <LastComment v-if="hoverPoint && !hideAll" :point="hoverPoint" class="max-tablet:!hidden" />
              </template>
            </DraftZoom>
          </div>
        </div>

        <div
          class="draft-view__image-presenter__blur pointer-events-none absolute w-full"
          :class="{ hide: !loading }"
          :style="{ 'max-width': `min(${imageScaledSize.width}px, 100%)`, 'max-height': `min(${imageScaledSize.height}px, 100vh)` }"
        >
          <div :style="{ 'padding-bottom': `${blurPercent}%` }" class="w-full"></div>
          <div class="draft-view__image-presenter__blur-image blur-[100px]" :style="{ 'background-image': `url(${blurImageUrl})` }"></div>
        </div>
      </div>

      <div v-if="draft" class="fixed bottom-xm right-xm max-tablet:hidden">
        <IconButton ref="showBottomHelpBtn" :shadow="false" :action="showBottomHelpClick" :active="showBottomHelp">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M19.5 10C19.5 15.2467 15.2467 19.5 10 19.5C4.75329 19.5 0.5 15.2467 0.5 10C0.5 4.75329 4.75329 0.5 10 0.5C15.2467 0.5 19.5 4.75329 19.5 10Z"
            />
            <path d="M10 9L10 16" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M10 4L10 6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </IconButton>

        <DraftHelp v-if="showBottomHelp" :draft="draft"></DraftHelp>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.draft-view {
  display: flex;
  height: 100%;
  max-height: 100vh;
  cursor: default;

  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1001;
  }

  &__btn {
    border-radius: 6px;
    height: 32px;
    width: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__image-presenter {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;

    &__blur {
      transition: opacity 0.8s;
      opacity: 1;
      overflow: hidden;
      //width: 100%;

      &.hide {
        opacity: 0;
      }

      &-image {
        display: block;
        position: absolute;
        width: 100%;
        height: 100%;
        left: 0;
        top: 0;
        background-size: cover;
        background-repeat: no-repeat;
      }
    }

    .draw-image {
      display: flex;
      width: 100%;
      height: 100%;
      position: relative;
      justify-content: center;
      align-items: center;
      transition: opacity 0.8s;
      opacity: 0;

      &.show {
        opacity: 1;
      }

      &__content {
        display: flex;
        position: absolute;
        //max-height: 100vh;
        //max-width: 100%;

        img {
          //max-width: 100%;
          //max-height: 100vh;
          pointer-events: none;
          //object-fit: contain;
          //image-rendering: pixelated;
          user-select: none;
        }

        &__draw {
          position: absolute;
          top: 0;
          left: 0;
          bottom: 0;
          right: 0;
        }
      }
    }
  }
}
</style>
